import classNames from "classnames";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { useCallback, useRef, useState } from "react";
import {
  lockBodyScroll,
  unlockBodyScroll,
} from "../contexts/NavigationContext";
import { ExternalVideoPlayer } from "../elements/ExternalVideoPlayer";
import { PlayIcon } from "../icons/PlayIcon";
import { IVideo } from "../shared/Visual";
import {
  HalfWidthClassName,
  MainShortWidthClassName,
  MainWidthClassName,
} from "../styles";

export const VideoHero: React.FC<{
  image: IGatsbyImageData;
  alt?: string | null;
  video: IVideo;
  externalVideoUrl?: string | null;
  title?: string | null;
  heading?: string | null;
}> = ({ image, alt, video, externalVideoUrl, title, heading }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showExternalPlayer, setShowExternalPlayer] = useState(false);
  const handleExternalClose = useCallback(() => {
    setShowExternalPlayer(false);
    unlockBodyScroll();
    const video = videoRef.current;
    if (video) {
      video
        .play()
        .then(() => {
          /* empty*/
        })
        .catch(console.warn);
    }
  }, []);
  const handleExternalPlay = useCallback(() => {
    setShowExternalPlayer(true);
    lockBodyScroll();
    const video = videoRef.current;
    if (video) {
      video.pause();
    }
  }, []);
  const handleLoaded = useCallback(() => setImageLoaded(true), []);
  const handleStartLoad = useCallback(
    ({ wasCached }: { wasCached?: boolean }) => {
      if (wasCached) setImageLoaded(true);
    },
    []
  );

  return (
    <>
      <section
        data-component="Video Hero"
        className={classNames("relative", "h-screen max-h-850px")}
      >
        <>
          <GatsbyImage
            onLoad={handleLoaded}
            onStartLoad={handleStartLoad}
            onError={handleLoaded}
            className="left-0 top-0 h-full w-full"
            style={{ position: "absolute" }}
            image={image}
            alt={alt ?? "Hero image"}
            objectFit="cover"
            loading="eager"
          />
          <video
            ref={videoRef}
            onPlay={handleLoaded}
            autoPlay={true}
            muted={true}
            loop={true}
            src={imageLoaded ? video.localFile.publicURL : undefined}
            className={classNames(
              "absolute left-0 top-0 h-full w-full object-cover"
            )}
          />
        </>
        <div className="page-grid max-width absolute bottom-0 left-0 right-0 top-0 h-full w-full pt-20px lg:pt-40px">
          {title &&
            !heading &&
            (externalVideoUrl ? (
              <button
                className={classNames(
                  "col-span-12 col-start-3 mb-50px flex items-end",
                  MainWidthClassName
                )}
                onClick={handleExternalPlay}
              >
                <h3 className="h3 col-span-6 mb-3 text-left text-hyacinth">
                  {title}
                </h3>
                <PlayIcon className="mb-3 ml-20px h-40px w-40px" />
              </button>
            ) : (
              <h1
                className={classNames(
                  MainWidthClassName,
                  "h1 col-span-4 col-start-2 mb-50px text-cotton"
                )}
              >
                {title}
              </h1>
            ))}
          {title && heading && (
            <>
              <h1
                className={classNames(
                  "h1 pt-40px text-hyacinth md:pt-100px lg:pt-100px",
                  HalfWidthClassName
                )}
              >
                {title}
              </h1>
              <h2
                className={classNames(
                  "h2 pt-20px text-cotton",
                  MainShortWidthClassName
                )}
              >
                {heading}
              </h2>
            </>
          )}
        </div>
      </section>
      {externalVideoUrl && (
        <ExternalVideoPlayer
          externalVideoUrl={externalVideoUrl}
          showExternalPlayer={showExternalPlayer}
          onClose={handleExternalClose}
        />
      )}
    </>
  );
};
